import type { AnyAction, Dispatch } from 'redux';

import * as FindExpenseReportsTableAPI from 'ab-api/web/order/findForExpenseReportTable';
import * as FindOrderReportsTableAPI from 'ab-api/web/order/findForOrderReportTable';
import * as GetTotalExpenseReportAmountAPI from 'ab-api/web/order/getTotalExpenseReportAmount';
import * as GetTotalOrderReportAmountAPI from 'ab-api/web/order/getTotalOrderReportAmount';

import type OrderTableVM from 'ab-viewModels/order/orderTable.viewModel';
import type OrderUpsertVM from 'ab-viewModels/order/orderUpsert.viewModel';

import type OrderUpsertRM from 'ab-requestModels/order/upsert.requestModel';

import type { GetRootState } from 'af-reducers';

import { errorHandler } from 'af-utils/actions.util';
import { http } from 'af-utils/http.util';

import type { OrderCompletionStatusFilter } from 'ab-enums/orderTypeFilter.enum';

import type { TableContent } from 'ab-common/dataStructures/tableContent';
import type { TableQuery } from 'ab-common/dataStructures/tableQuery';

import API from 'af-routes/api';

export function findForEdit(orderId: number) {
	return async (dispatch: Dispatch<AnyAction>, getState: GetRootState, { redirectTo }) => {

		const action = async () => {
			return await http.get<OrderUpsertVM>(API.V1.ORDERS.FIND_FOR_EDIT(orderId));
		};

		return await errorHandler(action, dispatch, redirectTo);
	};
}

/**
 *
 * @param tableQuery
 * @param startDate YYYY-MM-DD
 * @param endDate YYYY-MM-DD
 * @returns
 */
export function findForTable(
	tableQuery: TableQuery,
	completionStatus: OrderCompletionStatusFilter,
	startDate: Nullable<string>,
	endDate: Nullable<string>,
	timezone: Nullable<string>
) {
	return async (dispatch: Dispatch<AnyAction>, getState: GetRootState, { redirectTo }) => {
		const action = async () => {
			return await http.get<TableContent<OrderTableVM>>(API.V1.ORDERS.FIND_FOR_TABLE(tableQuery, completionStatus, startDate, endDate, timezone));
		};

		return await errorHandler(action, dispatch, redirectTo);
	};
}

export function findForOrdersReportTable(
	tableQuery: TableQuery,
	startDate: string,
	endDate: string,
	timezone: string,
	additionalFilter: FindOrderReportsTableAPI.W_Order_FindOrderReportTable_QP_AdditionalFilters
) {
	return async (dispatch: Dispatch<AnyAction>, getState: GetRootState, { redirectTo }) => {
		const action = async () => {
			return await http.get<TableContent<FindOrderReportsTableAPI.W_Order_FindOrderReportTable_VM_Row>>(
				FindOrderReportsTableAPI.URL(tableQuery, startDate, endDate, timezone, additionalFilter)
			);
		};

		return await errorHandler(action, dispatch, redirectTo);
	};
}

export function findForExpenseReportTable(
	tableQuery: TableQuery,
	startDate: string,
	endDate: string,
	timezone: string,
	additionalFilter: FindExpenseReportsTableAPI.W_Order_FindExpenseReportTable_QP_AdditionalFilters
) {
	return async (dispatch: Dispatch<AnyAction>, getState: GetRootState, { redirectTo }) => {
		const action = async () => {
			return await http.get<TableContent<FindExpenseReportsTableAPI.W_Order_FindExpenseReportTable_VM_Row>>(
				FindExpenseReportsTableAPI.URL(tableQuery, startDate, endDate, timezone, additionalFilter)
			);
		};

		return await errorHandler(action, dispatch, redirectTo);
	};
}

export function fetchTotalOrderReportAmount(
	tableQuery: TableQuery,
	startDate: string,
	endDate: string,
	timezone: string,
	additionalFilter: FindOrderReportsTableAPI.W_Order_FindOrderReportTable_QP_AdditionalFilters
) {
	return async (dispatch: Dispatch<AnyAction>, getState: GetRootState, { redirectTo }) => {
		const action = async () => {
			const result = await http.get<GetTotalOrderReportAmountAPI.W_Order_GetTotalOrderReportAmount_VM>(
				GetTotalOrderReportAmountAPI.URL(tableQuery, startDate, endDate, timezone, additionalFilter)
			);
			return { totalAmount: result.amount };
		};

		return await errorHandler(action, dispatch, redirectTo);
	};
}

export function fetchTotalExpenseReportAmount(
	tableQuery: TableQuery,
	startDate: string,
	endDate: string,
	timezone: string,
	additionalFilter: FindExpenseReportsTableAPI.W_Order_FindExpenseReportTable_QP_AdditionalFilters
) {
	return async (dispatch: Dispatch<AnyAction>, getState: GetRootState, { redirectTo }) => {
		const action = async () => {
			const result = await http.get<GetTotalExpenseReportAmountAPI.W_Order_GetTotalExpenseReportAmount_VM>(
				GetTotalExpenseReportAmountAPI.URL(tableQuery, startDate, endDate, timezone, additionalFilter)
			);
			return { totalAmount: result.amount };
		};

		return await errorHandler(action, dispatch, redirectTo);
	};
}

export function createOrder(rm: OrderUpsertRM) {
	return async (dispatch: Dispatch<AnyAction>, getState: GetRootState, { redirectTo }) => {

		const action = async () => {
			return await http.post(API.V1.ORDERS.CREATE, rm);
		};

		return await errorHandler(action, dispatch, redirectTo);
	};
}

export function editOrder(id: number, rm: OrderUpsertRM) {
	return async (dispatch: Dispatch<AnyAction>, getState: GetRootState, { redirectTo }) => {

		const action = async () => {
			return await http.put(API.V1.ORDERS.EDIT(id), rm);
		};

		return await errorHandler(action, dispatch, redirectTo);
	};
}

export function deleteOrder(id: number) {
	return async (dispatch: Dispatch<AnyAction>, getState: GetRootState, { redirectTo }) => {

		const action = async () => {
			return await http.delete(API.V1.ORDERS.DELETE(id));
		};

		return await errorHandler(action, dispatch, redirectTo);
	};
}
